import type React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import { Link, Navigate } from 'react-router-dom';

import { routes } from 'app/routes/constants';
import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import ActiveTrainingPlanRail from 'app/ui/components/molecules/active-training-plan-rail';

import { ActiveTrainingPlans as ACTIVE_TRAINING_PLANS } from 'app/pages/active-training-plans/query.gql';
import type {
  ActiveTrainingPlansQuery,
  ActiveTrainingPlansQueryVariables,
  ProgressivePlanInstance,
} from 'app/types/graphql';

const BreadcrumbWrapper = styled.div`
  display: flex;
  margin-top: ${({ theme: { spacing } }) => spacing(10)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(4)};
`;

const StyledResourceHeader = styled(ResourceHeader)`
  margin-bottom: ${({ theme }) => theme.spacing(8)};

  ${({ theme: { media } }) => `${media.medium(() => 'margin-bottom: 0;')}`};
`;

const RailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(12)};
`;

const PlanTitle = styled(Link)`
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  text-decoration: none;
  display: block;
`;

const ActiveTrainingPlansPage: React.FC = () => {
  const intl = useIntl();

  const { data, loading, error } = useQuery<ActiveTrainingPlansQuery, ActiveTrainingPlansQueryVariables>(
    ACTIVE_TRAINING_PLANS,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  if (loading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ErrorComponent />;
  }

  const planInstances = data.allProgressivePlanInstances?.planInstances || [];

  if (!planInstances.length) {
    return <Navigate to={routes.TRAINING_PLANS} replace={true} />;
  }

  const pageTitle = intl.formatMessage({ id: 'library.activeTrainingPlans' });

  return (
    <GenericPage pageTitle={pageTitle}>
      <StyledResourceHeader
        title={pageTitle}
        content={intl.formatMessage({ id: 'library.trainingPlansCopy' })}
        imageUrl="/plans-bg.png"
      />
      <BreadcrumbWrapper>
        <Breadcrumb contentTitle={intl.formatMessage({ id: 'breadcrumb.active' })} />
      </BreadcrumbWrapper>
      <ContentWrapper>
        <RailWrapper>
          {planInstances.map((planInstance) => (
            <div key={planInstance.id}>
              <PlanTitle to={routes.TRAINING_PLAN_ACTIVE_INSTANCE.replace(':id', planInstance.id)}>
                {planInstance.progressivePlan?.name}
              </PlanTitle>
              <ActiveTrainingPlanRail planInstance={planInstance as ProgressivePlanInstance} />
            </div>
          ))}
        </RailWrapper>
      </ContentWrapper>
    </GenericPage>
  );
};

export default ActiveTrainingPlansPage;
